<template>
    <div class="account-statement">
        <form @submit.prevent="getData" class="main-form">
            <custom-select
                v-if="isSupervisor"
                :options="webshopOptions"
                :title="$t('REPORT__WEBSHOP')"
                v-model="requestParams.WebShopId"
                @clear="requestParams.WebShopId = ''"
            />
            <date-picker 
                v-model="requestParams.StartDate"
                :title="$t('REPORT__START_DATE')"
                :enableTime=true
                @clear="requestParams.StartDate = ''"
            />
            <date-picker 
                v-model="requestParams.ToDate"
                :title="$t('REPORT__TO_DATE')"
                :enableTime=true
                @clear="requestParams.ToDate = ''"
            />
            <button class="btn">{{ $t('REPORT__SEARCH') }}</button>
        </form>
        <div class="loader" v-show="loading"></div>
        <grid
            v-show="!loading"
            fullGrid
            title="Revenue By Game Type"
            :passedData="revenueByGameTypeData"
            :columns="gameTypeRevenueColumns"
            :pagination=false
            @sort="sortGameTypeGrid"
        />
        <div class="totals-container" v-show="!loading">
            <div class="total-item">
                <h5 class="title">Total Bet Amount</h5>
                <div class="spans">
                    <span class="value">{{ totalBetAmount }}</span>
                    <span class="currency">{{ currencyCode }}</span>
                </div>
            </div>
            <div class="total-item">
                <h5 class="title">Total Win Amount</h5>
                <div class="spans">
                    <span class="value">{{ totalWinAmount }}</span>
                    <span class="currency">{{ currencyCode }}</span>
                </div>
            </div>
            <div class="total-item">
                <h5 class="title">Total Profit</h5>
                <div class="spans">
                    <span class="value">{{ totalProfit }}</span>
                    <span class="currency">{{ currencyCode }}</span>
                </div>
            </div>
            <div class="total-item">
                <h5 class="title">Total Bonus Amount</h5>
                <div class="spans">
                    <span class="value">{{ totalBonusAmount }}</span>
                    <span class="currency">{{ currencyCode }}</span>
                </div>
            </div>
            <div class="total-item">
                <h5 class="title">Total NGR</h5>
                <div class="spans">
                    <span class="value">{{ totalNGR }}</span>
                    <span class="currency">{{ currencyCode }}</span>
                </div>
            </div>
        </div>
        <grid
            v-show="!loading"
            fullGrid
            title="Internal Revenue"
            :passedData="internalRevenueData"
            :columns="internalRevenueColumns"
            :pagination=false
        />
        <div class="totals-container" v-show="!loading">
            <div class="total-item">
                <h5 class="title">Total Card Revenue</h5>
                <div class="spans">
                    <span class="value">{{ totalCardRevenue }}</span>
                    <span class="currency">{{ currencyCode }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import DatePicker from '@/components/ReusableComponents/DatePicker/DatePicker.vue'
import CustomSelect from '@/components/ReusableComponents/CustomSelect/CustomSelect.vue'
import Grid from '@/components/ReusableComponents/Grid/Grid.vue'
import axios from '@/Helpers/AxiosInstance.js'
import { getDateString } from '@/Helpers/HelperFunctions.js'
export default {
    components: {
        DatePicker, CustomSelect, Grid,
    },
    created() {
        this.setDefaultDates()
        this.getData()
    },
    data() {
        return {
            requestParams: {
                WebShopId: '',
                StartDate: '',
                ToDate: '',
            },
            gameTypeRevenueColumns: [
                {
                    title: this.$t('REPORT__GAME_TYPE'),
                    key: 'gameType',
                },
                {
                    title: this.$t('REPORT__BET_AMOUNT'),
                    key: 'betAmount',
                    customTemplate: (item, val) => val + ' ' + item.currencyCode,
                },
                {
                    title: this.$t('REPORT__WIN_AMOUNT'),
                    key: 'winAmount',
                    customTemplate: (item, val) => val + ' ' + item.currencyCode,
                },
                {
                    title: 'GGR',
                    key: 'ggr',
                    customTemplate: (item, val) => val + ' ' + item.currencyCode,
                },
                {
                    title: this.$t('REPORT__PROFITABILITY'),
                    key: 'profitability',
                    customTemplate: (item, val) => Math.floor(val * 10000) / 100 + '%'
                },
            ],
            revenueByGameTypeData: [],
            internalRevenueData: [],
            loading: false,
            orderColumn: 'gameType',
            orderDirection: 'desc',
            internalRevenueColumns: [
                {
                    title: 'Active Users',
                    key: 'activeUsers',
                },
                {
                    title: 'Activated Prepaid Cards',
                    key: 'activatedPrepaidCards',
                    customTemplate: (item, val) => val ? val : 0
                },
                {
                    title: 'Confirmed Withdraws',
                    key: 'confirmedWithdraws',
                    customTemplate: (item, val) => val + ' ' + item.currencyCode,
                },
                {
                    title: 'Direct PayIns',
                    key: 'directPayIns',
                    customTemplate: (item, val) => val + ' ' + item.currencyCode,
                },
                {
                    title: 'Direct Withdraws',
                    key: 'directWithdraws',
                    customTemplate: (item, val) => val + ' ' + item.currencyCode,
                },
            ],
            totalBetAmount: 0,
            totalWinAmount: 0,
            totalProfit: 0,
            totalBonusAmount: 0,
            totalNGR: 0,
            currencyCode: '',
            totalCardRevenue: 0,
        }
    },
    methods: {
        getData() {
            this.loading = true
            //setting mnutes to be always 00
            var startDate = getDateString(this.requestParams.StartDate, 'yyyy-MM-dd HH:mm')
            startDate = startDate.split(':')[0] + ':00'
            var toDate = getDateString(this.requestParams.ToDate, 'yyyy-MM-dd HH:mm')
            toDate = toDate.split(':')[0] + ':00'
            axios
                .get('WebShop/GetShopRevenueReportByGameType', {params : {
                    WebShopId: this.requestParams.WebShopId,
                    StartDate: startDate,
                    ToDate: toDate,
                    OrderColumn: this.orderColumn,
                    OrderDirection: this.orderDirection,
                }})
                .then(resp => {
                    if(resp.data.status == 1) {
                        let response = resp.data.response
                        this.revenueByGameTypeData = response
                        this.totalBetAmount = response.length > 0 && response[0].totalBetAmount ? response[0].totalBetAmount : 0
                        this.totalWinAmount = response.length > 0 && response[0].totalWinAmount ? response[0].totalWinAmount : 0
                        this.totalProfit = response.length > 0 && response[0].totalProfit ? response[0].totalProfit : 0
                        this.totalBonusAmount = response.length > 0 && response[0].totalBonusAmount ? response[0].totalBonusAmount : 0
                        this.totalNGR = response.length > 0 && response[0].totalNGR ? response[0].totalNGR : 0
                        this.currencyCode = response.length > 0 ? response[0].currencyCode : ''
                        this.loading = false
                    }
                })
            axios
                .get('WebShop/GetShopInternalRevenueReport', {params : {
                    WebShopId: this.requestParams.WebShopId,
                    FromDateReport: startDate,
                    ToDateReport: toDate,
                }})
                .then(resp => {
                    if(resp.data.status == 1) {
                        let response = resp.data.response
                        this.internalRevenueData = response
                        this.totalCardRevenue = response.length > 0 && response[0].totalCardRevenue ? response[0].totalCardRevenue : 0
                        this.loading = false
                    }
                })
        },
        sortGameTypeGrid(data) {
            this.orderColumn = data.columnName
            this.orderDirection = data.orderByDesc ? 'desc' : 'asc'
            this.getData()
        },
        setDefaultDates(){
            var d1 = new Date()
            this.requestParams.ToDate = d1
            var d2 = new Date
            d2.setDate(1)
            d2.setHours(0)
            d2.setMinutes(0)
            this.requestParams.StartDate = d2
        }
    },
    computed: {
        isSupervisor() {
            return this.$store.state.shop
        },
        webshopOptions() {
            var options = []
            this.$store.state.webshops.forEach(shop => {
                let title = shop.name
                    ? `${shop.calculatedName} (${shop.name})`
                    : shop.calculatedName
                options.push({ title: title, value: shop.id + '' })
            })
            return options
        },
    }
}
</script>

<style scoped lang="scss">
form {
    display: flex;
    align-items: center;
    margin-bottom: 15 * $rem;
    max-width: 1000 * $rem;

    .select-wrapper, label {
        margin-right: 15 * $rem;
    }
}

.totals-container {
    margin: 15 * $rem 0 30 * $rem 0;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;

    .total-item {
        margin-top: 15 * $rem;
        padding: 20 * $rem 50 * $rem;
        background-color: #fff;
        border: $rem solid #e3e4eb;
        border-radius: 5 * $rem;
        width: 270 * $rem;

        h5 {
            color: rgb(83, 83, 83);
            font-weight: 400;
            font-size: 16 * $rem;
            letter-spacing: 0.9 * $rem;
            margin-bottom: 5 * $rem;
        }

        span.currency {
            margin-left: 5 * $rem;

        }

        span.value {
            font-weight: 500;
        }
    }
}

@media only screen and (max-width: 465px) {
    .totals-container .total-item {
        width: 100% !important;

        h5 {
            text-align: center;
            font-size: 20 * $rem;
        }

        .spans {
            display: flex;
            justify-content: center;
            font-size: 16px;
        }
    }
}
</style>